import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  // Same-origin cookies are included by default. We keep withCredentials false
  // because the outer proxy adds "Access-Control-Allow-Origin: *" which browsers
  // treat as invalid when combined with credentials mode, blocking cookies.
  withCredentials: false,
});

// Read the non-httpOnly CSRF cookie set alongside the session cookies.
const readCsrfCookie = () => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match('(?:^|; )cognietica_csrf=([^;]*)');
  return m ? decodeURIComponent(m[1]) : null;
};

// Attach X-CSRF-Token to every state-changing request (double-submit pattern).
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const token = readCsrfCookie();
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

// Handle auth errors with silent-refresh + single retry, avoiding hard reload
// loops. Only endpoints that are truly unauthenticated bounce the user back to
// login.
let refreshPromise = null;
const _isAuthEndpoint = (url) => {
  const u = (url || '').toString();
  return u.includes('/auth/login') || u.includes('/auth/refresh') || u.includes('/auth/me');
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (status === 401 && original && !original._retry && !_isAuthEndpoint(original.url)) {
      original._retry = true;
      try {
        // Coalesce concurrent refreshes into a single request.
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh').finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        // Tell AuthContext the refresh cookie just slid forward another 30min,
        // so its local session-expiry warning timer doesn't fire on an active user.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('auth:session-refreshed'));
        }
        return api(original);
      } catch (_) {
        // fall through to redirect below
      }
    }

    if (status === 401 && !_isAuthEndpoint(original?.url)) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    // Normalise FastAPI 422 validation errors so consumers can safely render
    // `err.response.data.detail` as a string. Pydantic returns an array of
    // objects ({type, loc, msg, ctx, input}) which crashes React when rendered
    // directly ("Objects are not valid as a React child"). We flatten to a
    // readable "field: msg; field: msg" string.
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      try {
        const flat = error.response.data.detail
          .map((d) => {
            if (!d) return null;
            if (typeof d === 'string') return d;
            const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
            const msg = d.msg || d.message || 'valor inválido';
            return field ? `${field}: ${msg}` : msg;
          })
          .filter(Boolean)
          .join(' · ');
        error.response.data.detail = flat || 'Dados inválidos';
      } catch (_) {
        error.response.data.detail = 'Dados inválidos';
      }
    } else if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
      // Guard against any other non-string detail shape.
      error.response.data.detail =
        error.response.data.detail.msg || error.response.data.detail.message || 'Erro no servidor';
    }
    return Promise.reject(error);
  }
);

// Public endpoints
export const publicApi = {
  getTenant: (slug) => api.get(`/public/tenant/${slug}`),
  getTenantBranding: (slug) => api.get(`/public/tenant/${slug}/branding`),
  submitReport: (tenantSlug, data) => api.post(`/public/report/${tenantSlug}`, data),
  trackReport: (data) => api.post('/public/track', data),
  sendReporterMessage: (trackingCode, pin, content) => 
    api.post(`/public/track/${trackingCode}/message?pin=${pin}`, { content }),
  uploadReporterAttachment: (trackingCode, pin, file) => {
    const formData = new FormData();
    formData.append('pin', pin);
    formData.append('file', file);
    return api.post(`/public/track/${trackingCode}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (attachmentId, trackingCode, pin) => 
    `${BACKEND_URL}/api/public/attachment/${attachmentId}?tracking_code=${trackingCode}&pin=${pin}`,
  previewAttachment: (attachmentId, trackingCode, pin) =>
    `${BACKEND_URL}/api/public/attachment/${attachmentId}/preview?tracking_code=${trackingCode}&pin=${pin}`
};

// Auth endpoints
export const authApi = {
  login: (email, password, tenantSlug, mfaCode = null) => 
    api.post('/auth/login', { email, password, tenant_slug: tenantSlug, mfa_code: mfaCode }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  setupMfa: () => api.post('/auth/mfa/setup'),
  verifyMfa: (code) => api.post('/auth/mfa/verify', { code }),
  disableMfa: (code) => api.post('/auth/mfa/disable', { code }),
  regenerateBackupCodes: (code) => api.post('/auth/mfa/regenerate-backup-codes', { code })
};

// Admin endpoints
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getReports: (params) => api.get('/admin/reports', { params }),
  getReport: (id) => api.get(`/admin/reports/${id}`),
  updateReport: (id, data) => api.patch(`/admin/reports/${id}`, data),
  reopenReport: (id, reason, notifyReporter = true) => api.post(`/admin/reports/${id}/reopen`, { reason, notify_reporter: notifyReporter }),
  approveWorkflow: (id, action, comments) => api.post(`/admin/reports/${id}/workflow/approve`, { action, comments }),
  getReportWorkflow: (id) => api.get(`/admin/reports/${id}/workflow`),
  sendAcknowledgment: (id) => api.post(`/admin/reports/${id}/acknowledge`),
  sendMessage: (reportId, content) => api.post(`/admin/reports/${reportId}/message`, { content }),
  uploadAttachment: (reportId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/reports/${reportId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (attachmentId) => api.get(`/admin/attachment/${attachmentId}`, { responseType: 'blob' }),
  previewAttachment: (attachmentId) => `${BACKEND_URL}/api/admin/attachment/${attachmentId}/preview`,
  deleteAttachment: (attachmentId) => api.delete(`/admin/attachment/${attachmentId}`),
  exportPdf: (reportId) => api.get(`/admin/reports/${reportId}/pdf`, { responseType: 'blob' }),
  getUsers: () => api.get('/admin/users'),
  bulkAssignReports: (payload) => api.post('/admin/reports/bulk-assign', payload),
  getAnalyticsByManager: () => api.get('/admin/analytics/by-manager'),
  getReportTimeline: (reportId) => api.get(`/admin/reports/${reportId}/timeline`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  sendUserResetLink: (userId) => api.post(`/admin/users/${userId}/send-reset-link`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.patch('/admin/settings', data),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),

  // Billing & add-ons (tenant)
  getBilling: () => api.get('/admin/billing'),
  acceptAddon: (data) => api.post('/admin/billing/accept-addon', data),
  
  // Analytics & Export
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  exportReports: (format) => api.get(`/admin/reports/export/${format}`, { responseType: 'blob' }),
  
  // AI Triage
  analyzeReport: (reportId) => api.post(`/admin/reports/${reportId}/ai-analyze`),
  checkDuplicate: (description) => api.post('/admin/reports/check-duplicate', null, { params: { description } }),
  approveAISuggestions: (reportId, data) => api.post(`/admin/reports/${reportId}/ai-suggestions/approve`, data),
  
  // Workflow
  getWorkflowConfig: () => api.get('/admin/workflow/config'),
  updateWorkflowConfig: (data) => api.put('/admin/workflow/config', data),
  getReportWorkflow: (reportId) => api.get(`/admin/reports/${reportId}/workflow`),
  startWorkflow: (reportId) => api.post(`/admin/reports/${reportId}/workflow/start`),
  approveWorkflow: (reportId, data) => api.post(`/admin/reports/${reportId}/workflow/approve`, data),
  
  // Audit Logs
  getReportAuditLogs: (reportId) => api.get(`/admin/reports/${reportId}/audit-logs`),
  
  // Email Templates
  getEmailTemplates: () => api.get('/admin/email-templates'),
  createEmailTemplate: (data) => api.post('/admin/email-templates', data),
  updateEmailTemplate: (id, data) => api.patch(`/admin/email-templates/${id}`, data),
  deleteEmailTemplate: (id) => api.delete(`/admin/email-templates/${id}`),
  
  // Conflict of Interest
  getConflictRules: () => api.get('/admin/conflict-rules'),
  createConflictRule: (data) => api.post('/admin/conflict-rules', data),
  updateConflictRule: (id, data) => api.patch(`/admin/conflict-rules/${id}`, data),
  deleteConflictRule: (id) => api.delete(`/admin/conflict-rules/${id}`),
  getConflictAlerts: () => api.get('/admin/conflict-alerts'),
  resolveConflictAlert: (alertId, action, assigneeId = null) => 
    api.post(`/admin/conflict-alerts/${alertId}/resolve?action=${action}${assigneeId ? `&assignee_id=${assigneeId}` : ''}`),
  
  // Branding Settings
  updateBrandingSettings: (data) => api.patch('/admin/settings/branding', data),
  detectBrandColor: (url) => api.post('/admin/settings/detect-brand-color', { url }),
  uploadHeroImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/settings/hero-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/settings/logo-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Conflict Statistics
  getConflictStats: () => api.get('/admin/conflict-stats'),
  
  // Notifications
  getNotifications: (unreadOnly = false, limit = 50) => 
    api.get('/admin/notifications', { params: { unread_only: unreadOnly, limit } }),
  markNotificationRead: (id) => api.post(`/admin/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post('/admin/notifications/read-all'),
  subscribePush: (subscription) => api.post('/admin/push/subscribe', subscription),
  unsubscribePush: (endpoint) => api.delete('/admin/push/unsubscribe', { params: { endpoint } }),
  
  // Retention Configuration
  getRetentionConfig: () => api.get('/admin/retention-config'),
  updateRetentionConfig: (config) => api.put('/admin/retention-config', config),
  runArchiveJob: (dryRun = true) => api.post('/admin/retention/run-archive', null, { params: { dry_run: dryRun } }),
  runAnonymizeJob: (dryRun = true) => api.post('/admin/retention/run-anonymize', null, { params: { dry_run: dryRun } }),
  
  // Scheduled Reports
  getScheduledReportConfig: () => api.get('/admin/scheduled-reports/config'),
  updateScheduledReportConfig: (config) => api.put('/admin/scheduled-reports/config', config),
  sendScheduledReportNow: () => api.post('/admin/scheduled-reports/send-now')
};

// Super admin endpoints
export const superAdminApi = {
  getTenants: () => api.get('/super-admin/tenants'),
  createTenant: (data) => api.post('/super-admin/tenants', data),
  getTenant: (id) => api.get(`/super-admin/tenants/${id}`),
  updateTenant: (id, data) => api.patch(`/super-admin/tenants/${id}`, data)
};

// Setup endpoint
export const setupApi = {
  initialize: () => api.post('/setup')
};

export default api;
